import React, { useState } from 'react';
import { Play, Pause, Square, Volume2, VolumeX, Maximize2 } from 'lucide-react';
import { useZen } from '../contexts/ZenContext';
import { Slider } from './ui/slider';
import ImmersivePlayer from './ImmersivePlayer';
import useKeepAwake from '../hooks/useKeepAwake';
import KeepAwakeToggle from './KeepAwakeToggle';

const formatTime = (seconds) => {
  if (!seconds || isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export const AudioPlayer = () => {
  const [showImmersive, setShowImmersive] = useState(false);
  
  const {
    currentTrack,
    isPlaying,
    duration,
    currentTime,
    mainVolume,
    togglePlay,
    stopTrack,
    seekTo,
    updateMainVolume
  } = useZen();

  const [keepAwake, toggleKeepAwake] = useKeepAwake('lecteur', !!currentTrack && isPlaying);

  if (!currentTrack) {
    return (
      <div className="player-card p-6" data-testid="audio-player-empty">
        <h3 className="font-heading text-base sm:text-lg text-amber-300 mb-3 text-center">
          Comment démarrer votre séance
        </h3>
        <ol className="space-y-2.5 text-white/80 text-sm" data-testid="audio-player-howto">
          {[
            'Choisissez vos sons ambiants (panneau ci-dessous).',
            "Sélectionnez votre séance dans Mes dossiers ou l'onglet Audio.",
            'Installez-vous confortablement dans un endroit calme.',
            'Démarrez la séance et profitez pleinement de ce moment de relaxation.',
          ].map((step, i) => (
            <li key={step} className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/20 border border-amber-400/40 text-amber-300 text-xs font-semibold flex items-center justify-center mt-0.5">
                {i + 1}
              </span>
              <span className="leading-relaxed">{step}</span>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <>
      <div className="player-card p-6" data-testid="audio-player">
        {/* Track Info */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <h3 className="font-heading text-xl font-semibold text-white mb-1">
              {currentTrack.title}
            </h3>
            {currentTrack.description && (
              <p className="text-white/60 text-sm">{currentTrack.description}</p>
            )}
          </div>
          
          {/* Immersive Mode Button */}
          <button
            onClick={() => setShowImmersive(true)}
            className="p-2 rounded-lg bg-gradient-to-br from-amber-500/20 to-purple-500/20 border border-amber-500/30 text-amber-400 hover:bg-amber-500/30 hover:scale-105 transition-all"
            title="Mode immersif plein écran"
            data-testid="immersive-mode-btn"
          >
            <Maximize2 className="w-5 h-5" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-4">
          <div 
            className="relative h-2 bg-white/10 rounded-full cursor-pointer overflow-hidden"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const percent = (e.clientX - rect.left) / rect.width;
              seekTo(percent * duration);
            }}
            data-testid="progress-bar"
          >
            <div 
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-amber-400 to-purple-400 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs font-mono text-white/60">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={togglePlay}
              className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center text-slate-900 hover:scale-105 transition-transform shadow-lg shadow-amber-500/30"
              data-testid="play-pause-btn"
            >
              {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
            </button>
            <button
              onClick={stopTrack}
              className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/80 hover:bg-white/20 transition-colors"
              data-testid="stop-btn"
            >
              <Square className="w-4 h-4" />
            </button>
          </div>

          {/* Volume Control */}
          <div className="volume-control">
            <button
              onClick={() => updateMainVolume(mainVolume === 0 ? 0.7 : 0)}
              className="text-white/60 hover:text-white transition-colors"
              data-testid="mute-btn"
            >
              {mainVolume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <Slider
              value={[mainVolume * 100]}
              onValueChange={(value) => updateMainVolume(value[0] / 100)}
              max={100}
              step={1}
              className="w-24"
              data-testid="volume-slider"
            />
          </div>
        </div>
        
        {/* Hint for immersive mode */}
        {isPlaying && (
          <div className="mt-4 pt-4 border-t border-white/10 text-center">
            <button
              onClick={() => setShowImmersive(true)}
              className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
            >
              ✨ Cliquez ici pour le mode immersif avec fond nature animé
            </button>
          </div>
        )}

        {/* Screen sleep choice during playback */}
        <div className="mt-3 flex justify-center">
          <KeepAwakeToggle keepAwake={keepAwake} onToggle={toggleKeepAwake} testId="keep-awake-lecteur"
            className="px-4 py-2 rounded-full text-xs font-medium bg-white/10 border border-white/15 text-white/80 hover:bg-white/20" />
        </div>
      </div>

      {/* Immersive Player Modal - rendu direct avec CSS fixed */}
      {showImmersive && (
        <ImmersivePlayer onClose={() => setShowImmersive(false)} />
      )}
    </>
  );
};

export default AudioPlayer;
